import React from 'react';
import { TermDepositList, TermDepositsCsvRow } from '../../types';
import { Typography } from '@mui/material';
import { LoadingProgress } from '../../../../common/store/types';
import { DatatableColumn, FilterDataType } from '../../../../common/components/dataTable/types';
import { ServerSideDataTable } from '../../../../common/components/dataTable/serverSide';
import { PagedRequest, PagedResult } from '../../../../store';
import { formatDate, formatTerm, getMaturityColor } from 'src/common/utils/termDepositsFormatters';

export interface Props {
    pagedResults?: PagedResult<TermDepositList>;
    pagedRequest: PagedRequest;
    handlePagedRequest: (pagedRequest: PagedRequest) => void;
    progress: LoadingProgress;
    handleDownload: (pagedRequest: PagedRequest, mapper: (rows: TermDepositList[]) => TermDepositsCsvRow[]) => void;
}

export function TermDepositTable({
    pagedResults,
    pagedRequest,
    handlePagedRequest,
    handleDownload,
    progress
}: Props): JSX.Element {

    const onPaginationChange = (newPagedRequest: PagedRequest) => {
        handlePagedRequest(newPagedRequest);
    };

    const columns: DatatableColumn[] = [
        {
            label: 'CLIENT NAME',
            name: 'clientName',
            filterDataType: FilterDataType.string,
            options: {
                customBodyRender: function customBodyRender(value): React.ReactNode {
                    return (
                        <Typography
                            variant={'h5'}
                            style={{
                                letterSpacing: '1px',
                            }}>
                            {value ?? ''}
                        </Typography>
                    );
                },
                sort: true,
                viewColumns: true,
                filter: true
            },
        },
        {
            label: 'PORTFOLIO NAME',
            name: 'portfolioName',
            filterDataType: FilterDataType.string,
            options: {
                customBodyRender: (value) => (
                    <Typography>
                        {value ?? ''}
                    </Typography>
                ),
                sort: true,
                viewColumns: true,
                filter: true
            },
        },
        {
            label: 'ADVISER NAME',
            name: 'adviserName',
            filterDataType: FilterDataType.string,
            options: {
                customBodyRender: (value) => (
                    <Typography>
                        {value ?? ''}
                    </Typography>
                ),
                sort: true,
                viewColumns: true,
                filter: true
            },
        },
        {
            label: 'AFSL NAME',
            name: 'afslName',
            filterDataType: FilterDataType.string,
            options: {
                customBodyRender: (value) => (
                    <Typography>
                        {value ?? ''}
                    </Typography>
                ),
                sort: true,
                viewColumns: true,
                filter: true
            },
        },
        {
            label: 'SECURITY TYPE',
            name: 'securityType',
            filterDataType: FilterDataType.string,
            options: {
                customBodyRender: (value) => (
                    <Typography>
                        {value ?? ''}
                    </Typography>
                ),
                sort: true,
                viewColumns: true,
                filter: true
            },
        },
        {
            label: 'SECURITY CODE',
            name: 'securityCode',
            filterDataType: FilterDataType.string,
            options: {
                customBodyRender: (value) => (
                    <Typography>
                        {value ?? ''}
                    </Typography>
                ),
                sort: true,
                viewColumns: true,
                filter: true
            },
        },
        {
            label: 'VALUE',
            name: 'value',
            filterDataType: FilterDataType.string,
            options: {
                customBodyRender: (value) => (
                    <Typography>
                        {value != null ? `$${value}` : ''}
                    </Typography>
                ),
                sort: true,
                viewColumns: true,
                filter: true
            },
        },
        {
            label: 'INTEREST RATE',
            name: 'interestRate',
            filterDataType: FilterDataType.string,
            options: {
               customBodyRender: (value) => (
                    <Typography>
                        {value != null ? `${value}%` : ''}
                    </Typography>
                ),
                sort: true,
                viewColumns: true,
                filter: true
            },
        },
        {
            label: 'TERM',
            name: 'term',
            filterDataType: FilterDataType.string,
            options: {
                customBodyRender: (value) => (
                    <Typography>
                       {formatTerm(value) ?? ''}
                    </Typography>
                ),
                sort: true,
                viewColumns: true,
                filter: true
            },
        },
        {
            label: 'COMMENCEMENT DATE',
            name: 'commencementDate',
            filterDataType: FilterDataType.string,
            options: {
                customBodyRender: (value) => (
                    <Typography>
                        {formatDate(value) ?? ''}
                    </Typography>
                ),
                sort: true,
                viewColumns: true,
                filter: true
            },
        },
        {
            label: 'MATURITY DATE',
            name: 'maturityDate',
            filterDataType: FilterDataType.string,
            options: {
                customBodyRender: (value) => (
                    <Typography>
                        {formatDate(value) ?? ''}
                    </Typography>
                ),
                sort: false,
                viewColumns: true,
                filter: true
            },
        },
    ];

    return (
        <ServerSideDataTable
            pagedRequest={pagedRequest}
            pagedResult={pagedResults}
            columns={columns}
            options={{
                setRowProps: (_, dataIndex) => {
                     const maturityDate = pagedResults?.results?.[dataIndex]?.maturityDate;
                     const style = getMaturityColor(maturityDate);

                    return {
                        style: {backgroundColor: style?.backgroundColor},
                        className: style?.color === 'white' ? 'maturity-Off-white-text' : ''
                    };
                }
            }}
            loadingProgress={progress}
            handlePagedRequest={onPaginationChange}
            csvDownload={() =>
            handleDownload(
                pagedRequest,
                (termDeposits) =>
                    termDeposits.map((t) => ({
                        clientName: t.clientName,
                        portfolioName: t.portfolioName,
                        adviserName: t.adviserName,
                        securityType: t.securityType,
                        afslName: t.afslName,
                        securityCode: t.securityCode,
                        value: t.value,
                        interestRate: t.interestRate,
                        term: t.term,
                        commencementDate: t.commencementDate,
                        maturityDate: t.maturityDate,
                    }))
                )
            }
        />
    );
}
